import { MAIN } from './types.js';
import { plannedDestinations, roundsInOrder } from './schedule.js';
function overrideFor(overrides, schedule, pid, roundId) {
    const order = roundsInOrder(schedule).map((r) => r.roundId);
    const idx = order.indexOf(roundId);
    // Latest-created applicable override wins.
    return [...overrides]
        .filter((o) => o.participantId === pid)
        .filter((o) => {
        const from = order.indexOf(o.fromRoundId);
        return o.scope === 'current_round' ? from === idx : from >= 0 && from <= idx;
    })
        .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))[0];
}
export function effectiveDestinations(schedule, roundId, overrides) {
    const planned = plannedDestinations(schedule, roundId);
    const out = new Map();
    for (const [pid, dest] of planned) {
        const o = overrideFor(overrides, schedule, pid, roundId);
        if (!o)
            out.set(pid, dest);
        else if (o.action.kind === 'hold_in_main')
            out.set(pid, MAIN);
        else if (o.action.kind === 'redirect')
            out.set(pid, o.action.destinationRoomId);
        // exclude: not written at all
    }
    return out;
}
/**
 * Compute what the next round requires: only the writes that change
 * something, plus everything the operator must see before confirming
 * (spec section 10, step 2).
 */
export function computeRoundPlan(input) {
    const { schedule, roundId, previousRoundId, overrides, absent, excluded } = input;
    const roles = new Map(schedule.participants.map((p) => [p.participantId, p.role]));
    const next = effectiveDestinations(schedule, roundId, overrides);
    const prev = previousRoundId ? effectiveDestinations(schedule, previousRoundId, overrides) : new Map();
    const destinations = new Map();
    const changes = [];
    const unchanged = [];
    const absentOut = [];
    const excludedOut = [];
    for (const p of schedule.participants) {
        const pid = p.participantId;
        if (excluded.has(pid)) {
            excludedOut.push(pid);
            continue;
        }
        const to = next.get(pid);
        if (to === undefined) {
            // Excluded by override.
            excludedOut.push(pid);
            continue;
        }
        if (absent.has(pid)) {
            absentOut.push(pid);
            continue;
        }
        destinations.set(pid, to);
        const from = prev.get(pid) ?? MAIN;
        if (from !== to) {
            const planned = plannedDestinations(schedule, roundId).get(pid);
            changes.push({ participantId: pid, from, to, role: p.role, reason: planned === to ? 'schedule' : 'override' });
        }
        else
            unchanged.push(pid);
    }
    // Rooms with students but no present specialist.
    const studentsByRoom = new Map();
    const specialistPresentByRoom = new Map();
    for (const [pid, room] of destinations) {
        if (room === MAIN)
            continue;
        const role = roles.get(pid);
        if (role === 'student')
            studentsByRoom.set(room, [...(studentsByRoom.get(room) ?? []), pid]);
        if (role === 'specialist')
            specialistPresentByRoom.set(room, true);
    }
    const blockedRooms = [...studentsByRoom]
        .filter(([room]) => !specialistPresentByRoom.get(room))
        .map(([roomId, assignedStudents]) => ({ roomId, assignedStudents }));
    // Capacity after overrides.
    const counts = new Map();
    for (const [, room] of destinations)
        if (room !== MAIN)
            counts.set(room, (counts.get(room) ?? 0) + 1);
    const overCapacity = [...counts]
        .map(([roomId, count]) => ({ roomId, count, capacity: schedule.rooms.find((r) => r.roomId === roomId)?.capacity ?? Infinity }))
        .filter((x) => x.count > x.capacity);
    return { roundId, destinations, changes, unchanged, absent: absentOut, excluded: excludedOut, blockedRooms, overCapacity };
}
//# sourceMappingURL=diff.js.map